import { logger } from '@/utils/logger';

// Set this to true to enable debug logging
const DEBUG = false;

/**
 * Removes nullish values from objects in the recipe
 * Nullish values include: {}, '{}', "", null, undefined, []
 *
 * @param {Object} recipe - The recipe to clean
 * @param {boolean} removeKeysWithObjectButAllNullishValues - If true, removes objects that have keys but all values are nullish
 * @returns {Object} The cleaned recipe
 */

export const removeNullishValues = ({
  recipe,
  removeKeysWithObjectButAllNullishValues = false,
}) => {
  if (DEBUG) {
    logger.log('[removeNullishValues] Starting with recipe:', JSON.stringify(recipe, null, 2));
    logger.log(
      '[removeNullishValues] removeKeysWithObjectButAllNullishValues:',
      removeKeysWithObjectButAllNullishValues
    );
  }

  /**
   * Checks if a value is nullish according to our definition
   * @param {*} value - The value to check
   * @returns {boolean} True if the value is nullish
   */
  const isNullish = (value) => {
    const result =
      value === null ||
      value === undefined ||
      value === '' ||
      value === '{}' ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);
    if (DEBUG && result) {
      logger.log('[removeNullishValues] Detected nullish value:', value);
    }
    return result;
  };

  /**
   * Recursively cleans an object by removing nullish values
   * @param {*} obj - The object to clean
   * @returns {*} The cleaned object
   */
  const cleanObject = (obj, path = '') => {
    // Handle non-object types
    if (obj === null || obj === undefined) {
      if (DEBUG) logger.log(`[cleanObject] ${path} is null or undefined, returning undefined`);
      return undefined;
    }
    if (typeof obj !== 'object') {
      if (DEBUG) logger.log(`[cleanObject] ${path} is primitive (${typeof obj}), returning as is`);
      return obj;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      const cleanedArray = obj
        .map((item, idx) => cleanObject(item, `${path}[${idx}]`))
        .filter((item) => item !== undefined);
      if (DEBUG && cleanedArray.length !== obj.length) {
        logger.log(`[cleanObject] Cleaned array at ${path}:`, cleanedArray);
      }
      return cleanedArray.length > 0 ? cleanedArray : undefined;
    }

    // Handle objects
    const cleanedObj = {};
    let hasNonNullishValue = false;

    for (const [key, value] of Object.entries(obj)) {
      if (isNullish(value)) {
        if (DEBUG) logger.log(`[cleanObject] Skipping nullish key "${key}" at path "${path}"`);
        continue;
      }

      // Recursively clean nested objects
      const cleanedValue = cleanObject(value, path ? `${path}.${key}` : key);

      // Only add if the cleaned value is not undefined
      if (cleanedValue !== undefined) {
        cleanedObj[key] = cleanedValue;
        hasNonNullishValue = true;
      } else if (DEBUG) {
        logger.log(
          `[cleanObject] Value for key "${key}" at path "${path}" became undefined after cleaning`
        );
      }
    }

    // If flag is set and object has keys but all values are nullish, return undefined
    if (
      removeKeysWithObjectButAllNullishValues &&
      Object.keys(obj).length > 0 &&
      !hasNonNullishValue
    ) {
      if (DEBUG)
        logger.log(`[cleanObject] All values nullish at path "${path}", returning undefined`);
      return undefined;
    }

    // Return cleaned object if it has properties, otherwise return undefined
    if (Object.keys(cleanedObj).length > 0) {
      if (DEBUG) logger.log(`[cleanObject] Cleaned object at path "${path}":`, cleanedObj);
      return cleanedObj;
    }
    if (DEBUG)
      logger.log(`[cleanObject] No non-nullish values at path "${path}", returning undefined`);
    return undefined;
  };

  // Clean the entire recipe
  const cleanedRecipe = cleanObject(recipe);

  if (DEBUG) {
    logger.log(
      '[removeNullishValues] Final cleaned recipe:',
      JSON.stringify(cleanedRecipe, null, 2)
    );
  }

  // If the entire recipe becomes undefined, return an empty object
  return cleanedRecipe === undefined ? {} : cleanedRecipe;
};

// Example test cases for verification
// export const removeNullishValuesExamples = () => {
//   const examples = [
//     {
//       name: "Basic nullish values",
//       input: {
//         a: null,
//         b: undefined,
//         c: "",
//         d: "{}",
//         e: [],
//         f: {},
//         g: "valid value",
//         h: 123
//       },
//       expected: {
//         g: "valid value",
//         h: 123
//       }
//     },
//     {
//       name: "Nested objects with nullish values",
//       input: {
//         user: {
//           name: "John",
//           email: "",
//           metadata: {},
//           tags: [],
//           address: {
//             street: "",
//             city: "New York",
//             country: null
//           }
//         }
//       },
//       expected: {
//         user: {
//           name: "John",
//           address: {
//             city: "New York"
//           }
//         }
//       }
//     },
//     {
//       name: "Arrays with mixed values",
//       input: {
//         items: [null, "item1", {}, "", { name: "item2" }, [], undefined]
//       },
//       expected: {
//         items: ["item1", { name: "item2" }]
//       }
//     },
//     {
//       name: "Object with all nullish values (flag=false)",
//       input: {
//         config: {
//           a: null,
//           b: "",
//           c: {},
//           d: []
//         }
//       },
//       expected: {},
//       removeKeysWithObjectButAllNullishValues: false
//     },
//     {
//       name: "Object with all nullish values (flag=true)",
//       input: {
//         config: {
//           a: null,
//           b: "",
//           c: {},
//           d: []
//         },
//         validData: "keep this"
//       },
//       expected: {
//         validData: "keep this"
//       },
//       removeKeysWithObjectButAllNullishValues: true
//     },
//     {
//       name: "Deeply nested with mixed values",
//       input: {
//         level1: {
//           level2: {
//             level3: {
//               empty: {},
//               nullVal: null,
//               valid: "deep value"
//             }
//           },
//           emptyArray: [],
//           validArray: [1, 2, 3]
//         }
//       },
//       expected: {
//         level1: {
//           level2: {
//             level3: {
//               valid: "deep value"
//             }
//           },
//           validArray: [1, 2, 3]
//         }
//       }
//     },
//     {
//       name: "Recipe-like structure",
//       input: {
//         connectors: {
//           connector: [
//             {
//               id: "conn1",
//               name: "MySQL",
//               parameters: {},
//               metadata: null,
//               description: ""
//             },
//             {
//               id: "conn2",
//               name: "PostgreSQL",
//               parameters: {
//                 host: "localhost",
//                 port: 5432,
//                 username: "",
//                 password: null
//               }
//             }
//           ]
//         },
//         tasks: {
//           task: []
//         },
//         writeFormats: {}
//       },
//       expected: {
//         connectors: {
//           connector: [
//             {
//               id: "conn1",
//               name: "MySQL"
//             },
//             {
//               id: "conn2",
//               name: "PostgreSQL",
//               parameters: {
//                 host: "localhost",
//                 port: 5432
//               }
//             }
//           ]
//         }
//       }
//     }
//   ];

//   return examples;
// };
